/* Центр уведомлений. ТЗ 6.10, 7.10 — две вкладки: Критичные и Напоминания. */

import { canRead, daysWord, ddmm, sectionLevel } from "@mera/shared";
import React, { useState } from "react";
import { useNavigate } from "../lib/router.jsx";

import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelAlerts } from "../components/skeleton.jsx";
import { Empty, ErrorState, Sep, TabButton } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";

const ICONS = {
  task: Ion.checkbox,
  "deal-stale": Ion.funnel,
  dev: Ion.hardwareChip,
  payment: Ion.wallet,
  schedule: Ion.repeat,
  expense: Ion.trendingUp,
};

/** Куда ведёт алерт: у каждого вида своя карточка.

    Ведёт не всякий: задача, заведённая без сделки и проекта (личное напоминание
    из бота), живёт только в этом списке — открывать нечего. Регулярные расходы
    правятся в разделе для админов, остальным туда тоже некуда идти. Такие строки
    рисуем некликабельными, а не «нажал — ничего»: раньше они выглядели кнопками
    и молчали, и это читалось как поломка. */
const routeOf = (a, me) =>
  a.devTaskId ? `/devtask/${a.devTaskId}`
    : a.paymentId ? `/edit/payments/${a.paymentId}`
      : a.recurringId ? (me?.isAdmin ? "/manage/recurring" : null)
        : a.dealId ? `/deal/${a.dealId}`
          : a.projectId ? `/project/${a.projectId}`
            : null;

const whenText = (a) =>
  a.urg === "overdue" ? `просрочено на ${a.days} ${daysWord(a.days)}`
    : a.urg === "today" ? "сегодня"
      : a.urg === "stale" ? "без движения"
        : `через ${a.days} ${daysWord(a.days)}`;

export default function Alerts() {
  const { me, alerts, ready, reloadAlerts } = useApp();
  const [tab, setTab] = useState("critical");
  // Закрытые в этой сессии — прячем сразу, не дожидаясь ответа сервера
  const [closed, setClosed] = useState(() => new Set());
  const navigate = useNavigate();

  if (!ready) return <Screen logo><SkelAlerts /></Screen>;
  if (!canRead(sectionLevel(me, "alerts"))) {
    return <Screen logo><ErrorState text="Раздел закрыт администратором." /></Screen>;
  }

  const visible = (arr) => arr.filter((a) => !closed.has(`${a.level}:${a.key}`));
  const critical = visible(alerts.critical);
  const soft = visible(alerts.soft);
  const list = tab === "critical" ? critical : soft;
  const counts = { critical: critical.length, soft: soft.length };

  const open = (to) => {
    haptic.light();
    navigate(to);
  };

  /* Закрыть алерт: задача при этом отмечается выполненной, остальные виды
     просто перестают показываться (см. POST /alerts/close) */
  const close = async (a) => {
    haptic.success();
    setClosed((prev) => new Set(prev).add(`${a.level}:${a.key}`));
    try {
      await api.post("/alerts/close", { key: a.key, level: a.level });
      reloadAlerts();
    } catch {
      /* сеть моргнула — алерт вернётся при следующей загрузке */
    }
  };

  return (
    <Screen logo>
      {/* Две вкладки с раздельными счётчиками (ТЗ 6.10) */}
      <div className="px-4 pt-1 flex gap-2">
        <TabButton
          active={tab === "critical"}
          onClick={() => { haptic.select(); setTab("critical"); }}
          color="var(--fin-red)"
          label="Критичные"
          count={counts.critical}
        />
        <TabButton
          active={tab === "soft"}
          onClick={() => { haptic.select(); setTab("soft"); }}
          color="var(--fin-gold)"
          label="Напоминания"
          count={counts.soft}
        />
      </div>

      {!list.length ? (
        <Empty
          text={tab === "critical" ? "Критичных алертов нет — всё под контролем" : "Ближайших напоминаний нет"}
          icon={Ion.checkmarkCircle}
        />
      ) : (
        <div className="px-4 pt-3">
          <div className="card-tg py-1">
            {list.map((a, i) => {
              const Icon = ICONS[a.kind] || Ion.warning;
              const color = tab === "critical" ? "var(--fin-red)" : "var(--fin-gold)";
              const to = routeOf(a, me);
              return (
                <React.Fragment key={`${a.kind}-${a.dueDate}-${i}`}>
                  {i > 0 ? <Sep /> : null}
                  <div className="flex items-start">
                    <button
                      type="button"
                      disabled={!to}
                      className={`min-w-0 flex-1 text-left pl-4 py-3 flex items-start gap-3 ${to ? "active:opacity-60" : ""}`}
                      onClick={to ? () => open(to) : undefined}
                    >
                      <Icon size={17} style={{ color }} className="mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold leading-snug">{a.title}</div>
                        <div className="mt-0.5 text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                          {a.sub}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[12px]">
                          {a.date ? <span className="num" style={{ color }}>{a.date}</span> : null}
                          <span style={{ color }}>{whenText(a)}</span>
                        </div>
                      </div>
                      {to ? (
                        <Ion.chevronForward size={16} style={{ color: "var(--tg-hint)" }} className="mt-1" />
                      ) : null}
                    </button>
                    {/* Задачу галочка выполняет, прочие алерты — снимает с глаз */}
                    <button
                      type="button"
                      aria-label={a.kind === "task" ? "Выполнено" : "Закрыть"}
                      className="shrink-0 px-3 py-3 active:opacity-60"
                      onClick={() => close(a)}
                    >
                      <Ion.checkmarkCircle size={22} style={{ color: "var(--tg-hint)" }} className="mt-0.5" />
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

    </Screen>
  );
}

